import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { Intent } from '~/infra/access-control';
import { invoiceModel, timeModel } from '~/models';
import { InvoiceStatus } from '~/models/invoice';
function isPaymentDetailWithDate(detail) {
    return 'date' in detail;
}
const displayedStatusToLabel = {
    issued: 'Issued',
    overdue: 'Overdue',
    paymentInitiated: 'Processing',
    paid: 'Paid',
    voided: 'Voided',
    refunded: 'Refunded',
    partiallyRefunded: 'Partially refunded',
    refundFailed: 'Refund failed',
    disputed: 'Disputed'
};
export function getStatusLabel(displayedStatus) {
    return displayedStatusToLabel[displayedStatus];
}
const displayedStatusV2ToLabel = {
    issued: 'Issued',
    pending: 'Pending',
    overdue: 'Overdue',
    processing: 'Processing',
    paid: 'Paid',
    voided: 'Voided',
    refunded: 'Refunded',
    partiallyRefunded: 'Partially refunded',
    refundFailed: 'Refund failed',
    disputed: 'Disputed'
};
export function getStatusV2Label(displayedStatus) {
    return displayedStatusV2ToLabel[displayedStatus];
}
const paymentDetailTypeToLabel = {
    missingPaymentMethod: 'Missing payment method',
    paymentMethodNotVerified: 'Payment method unverified',
    clientApprovalRequired: 'Client approval required',
    paymentStopped: 'Payment stopped',
    paymentValidationRequired: 'Payment validation required',
    paymentAwaitingInitiation: 'Payment awaiting initiation',
    paymentScheduled: 'Payment will be processed automatically',
    paymentInitiated: 'Payment initiated',
    paymentCollected: 'Payment collected',
    refundRequested: 'Refund requested',
    refunded: 'Refunded',
    paidExternally: 'Paid externally',
    paymentFailed: 'Payment failed',
    paidOnDate: 'Paid',
    voided: 'Voided',
    disputed: 'Disputed',
    refundFailed: 'Refund failed'
};
const paymentDetailTypeToTooltip = {
    paymentAwaitingInitiation: 'Automatic payment processing will begin shortly',
    paymentInitiated: 'Processing the payment may take up to 3 business days',
    paymentCollected: 'The payment will be deposited in your bank account within 1-2 business days',
    refundRequested: 'Upon completion of payment processing, a refund will be issued'
};
export function getPaymentDetailTooltip(details) {
    for (const detail of details){
        const tooltip = paymentDetailTypeToTooltip[detail.type];
        if (tooltip) {
            return tooltip;
        }
    }
    return '';
}
export function getPaymentDetailLabel(detail) {
    const label = paymentDetailTypeToLabel[detail.type];
    if (isPaymentDetailWithDate(detail) && detail.date) {
        const numericDate = Number(detail.date);
        const formattedDate = isNaN(numericDate) ? timeModel.formatDateLong(new Date(detail.date)) : timeModel.formatDateLong(numericDate);
        return "".concat(label, " on ").concat(formattedDate);
    }
    return label;
}
export function formatPaymentDetailsList(details) {
    return details.map(getPaymentDetailLabel).map((label, index)=>index === 0 ? label : label.charAt(0).toLowerCase() + label.slice(1)).join(', ');
}
export function getPaymentStatusText(invoice, isVendor) {
    const statusType = invoiceModel.statusType(invoice);
    const { paymentDisplayStatus: paymentStatus } = invoice;
    switch(paymentStatus.type){
        case InvoicePaymentDisplayStatusType.None:
            return '';
        case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
            return 'Payment method not set';
        case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
            return 'Payment method not verified';
        case InvoicePaymentDisplayStatusType.PaymentFailed:
            return 'Payment failed';
        case InvoicePaymentDisplayStatusType.Paid:
            {
                const formattedDate = timeModel.formatDate(paymentStatus.timestamp);
                return paymentStatus.paidInFutureDay ? "Deposit expected on ".concat(formattedDate) : "Deposited on ".concat(formattedDate);
            }
        case InvoicePaymentDisplayStatusType.PaidExternally:
            return "Paid externally on ".concat(timeModel.formatDate(paymentStatus.timestamp));
        case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
            return isVendor ? 'Client approval required' : 'Pending your approval';
        case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
            return 'Payment validation required';
        case InvoicePaymentDisplayStatusType.Pending:
            return 'Payment pending initiation';
        case InvoicePaymentDisplayStatusType.Processing:
            return 'Payment initiated';
        case InvoicePaymentDisplayStatusType.Collected:
            return 'Payment collected';
        case InvoicePaymentDisplayStatusType.Refunded:
            {
                return "".concat(statusType === InvoiceStatus.PartiallyRefunded ? 'Partially refunded' : 'Refunded', " on ").concat(timeModel.formatDate(paymentStatus.timestamp));
            }
        case InvoicePaymentDisplayStatusType.RefundFailed:
            return "Refund failed on ".concat(timeModel.formatDate(paymentStatus.timestamp));
        case InvoicePaymentDisplayStatusType.StoppedPayment:
            {
                const date = timeModel.formatDate(paymentStatus.timestamp);
                return isVendor ? "Client stopped payment on ".concat(date) : "Payment stopped on ".concat(date);
            }
        case InvoicePaymentDisplayStatusType.Disputed:
            return "Disputed on ".concat(timeModel.formatDate(paymentStatus.timestamp));
        case InvoicePaymentDisplayStatusType.RefundRequested:
            return 'Refund requested';
    }
}
export function allowResendAction(status, isVendor) {
    return isVendor && status !== InvoiceStatus.PaymentDisputed;
}
export function allowVoidAction(status, isVendor) {
    if (!isVendor) return false;
    switch(status){
        case InvoiceStatus.RequiresPaymentApproval:
        case InvoiceStatus.PaymentFailed:
        case InvoiceStatus.PaymentStopped:
        case InvoiceStatus.Pending:
        case InvoiceStatus.PaymentActionRequired:
        case InvoiceStatus.PaidExternally:
        case InvoiceStatus.PaymentDisputed:
            return true;
        default:
            return false;
    }
}
export function allowUnvoidAction(invoice, isVendor) {
    return isVendor && invoiceModel.isVoided(invoice);
}
export function allowUnmarkPaidExternallyAction(invoice, isVendor) {
    return isVendor && invoiceModel.isPaidExternally(invoice);
}
export function getRefundActionConfig(param) {
    let { invoice, isVendor } = param;
    const showAction = isVendor && invoiceModel.rawTotalAmount(invoice).gt(0) && (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice) || invoiceModel.isProcessing(invoice) || invoiceModel.isPartiallyRefunded(invoice) || invoiceModel.isRefundFailed(invoice.paymentDisplayStatus)) && !invoiceModel.isRefundRequested(invoice.paymentDisplayStatus);
    const daysSincePayment = invoiceModel.daysSincePayment(invoice);
    const payment = invoiceModel.payment(invoice);
    const isOver90Days = payment && payment.isPaymentProcessorStripe() && !!daysSincePayment && daysSincePayment > 90;
    const disabled = !payment || !!isOver90Days;
    const tooltipTitle = isOver90Days ? "This payment can't be refunded—the payment processor's 90-day window has ended." : undefined;
    return {
        showAction,
        disabled,
        tooltipTitle,
        text: 'Refund payment',
        intent: Intent.RequestRefund
    };
}
